import React, { PureComponent } from 'react';
import { 
  Card, Divider, Grid, Button, Tab, Tabs, List, ListItem, Typography, 
  Tooltip, FormControl, FormControlLabel, FormLabel, Select, OutlinedInput, MenuItem, 
  Checkbox, RadioGroup, Radio, Dialog, DialogTitle, DialogContent, DialogActions
  } from '@material-ui/core';
import { withStyles } from '@material-ui/core';
import { FilterList } from '@material-ui/icons';
import { DatePicker } from 'material-ui-pickers';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import moment from 'moment-timezone';

import ComboBoxSearch from './ComboBoxSearch';
import GameCard from './cards/GameCard';
import WeatherContainer from '../containers/WeatherContainer';
import MentorMenteeCardContainer from '../containers/MentorMenteeCardContainer';
import UserStatisticsContainer from '../containers/UserStatisticsContainer';

const styles = theme => ({
  root: {
    margin: 'auto',
    padding: 16,
    backgroundColor: theme.palette.background.paper,
    maxWidth: 1280
  },
  gamesCard: {
    height: 752,
  },
  gameTable: {
    height: 672,
    overflow: 'scroll',
  },
  button: {
    margin: 0,
    padding: 0,
    fontSize: 14,
    width: 40,
  },
  divider: {
    height: 8,
    alignItems: 'center',
  },
  gamesHeader: {
    display: 'flex',
    direction: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    height: 48,
    padding: 8,
    fontSize: 18
  },
  groupControl: {
    margin: theme.spacing.unit,
    minWidth: 160,
  },
  dialogSection: {
    maxWidth: 460
  },
  dialogActions: {
    maxWidth: 460,
    margin: '0px 20px 14px'
  },
  formControlLabel: {
    justifyContent: 'flex-end',
    margin: '8px 8px 0px 8px',
    minWidth: 280,
  },
  selectLabel: {
    marginRight: 8
  },
  select: {
    minWidth: 200,
    padding: 8
  },
  smallSelect: {
    minWidth: 60,
    padding: 8
  },
  selectControl: {
    padding: '4px 12px 4px 12px'
  }
});

class Home extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      filters: {
        startDate: moment().format(),
        endDate: moment().add(7, 'days').format(),
        interval: '7',
        division: '',
        team: '',
        umpire: '',
        allGames: false,
        levelNeeded: '',
        levelAssigned: ''
      },
      selectedOption: '',
      tab: 'home-opening-tab',
      displayAdditionalGames: false,
      updatingAny: false,
    };
  }

  componentDidMount() {
    const {
      getUmpInfo,
      getSeasonDivisions,
      getCoachesTeams,
      memberId,
      userSeason
    } = this.props;

    const isCoach = this.isCoach();
    const isUmpire = this.isUmpire();

    getUmpInfo();

    if (userSeason) {
      getCoachesTeams(memberId, userSeason);
      getSeasonDivisions(userSeason);
      this.updateMyGames();
      this.handleFilterGames();
    }

    //Initialize the tab to display
    let openTab;
    if (isCoach || isUmpire) openTab = 'home-opening-tab';
    else openTab = 'home-selection-tab';
    this.setState({
      tab: openTab
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps !== this.props) {
      const {
        fetchingGamesStatus, fetchingMyGamesStatus, fetchingFilteredGamesStatus,
        fetchingCoachTeamsStatus, fetchingSeasonDivisionsStatus, fetchingUmpiresStatus,
        teamsVolunteeringStatus,
        getCoachesTeams, teamsCoaching, getTeamsVolunteering, 
        getSeasonDivisions,
        memberId,
        userSeason
      } = this.props;

      let isUpdating =
        fetchingGamesStatus === 'Loading' || fetchingMyGamesStatus === 'Loading' ||
        fetchingFilteredGamesStatus === 'Loading' || fetchingCoachTeamsStatus === 'Loading' ||
        fetchingSeasonDivisionsStatus === 'Loading' || fetchingUmpiresStatus === 'Loading' ||
        teamsVolunteeringStatus === 'Loading';

      //On change of Season, load Divisions, Teams and Games
      if ((userSeason) && (userSeason !== prevProps.userSeason)) {
        getCoachesTeams(memberId, userSeason);
        getTeamsVolunteering(memberId, userSeason);
        getSeasonDivisions(userSeason);
        this.handleFilterGames();
        this.updateMyGames();
        isUpdating = true;
      }

      //On change of Teams list reload "My Games"
      if ((teamsCoaching !== prevProps.teamsCoaching) && this.isCoach()) {
        this.updateMyGames();
        isUpdating = true;
      }

      this.setState({
        updatingAny: isUpdating
      });
    }
  }

  isAdmin = () => {
    return this.props.userType === 'Admin';
  }

  isUmpire = () => {
    return this.props.umpireLevel !== null && this.props.umpireLevel > 0;
  }

  isCoach = () => {
    return this.props.teamsCoaching.length > 0;
  }

  allLoaded = () => {
    //Check that all required state objects have been loaded
    const {
      fetchingMyGamesStatus, fetchingFilteredGamesStatus,
      fetchingCoachTeamsStatus, fetchingSeasonDivisionsStatus, fetchingUmpiresStatus
    } = this.props;
    return  fetchingMyGamesStatus === 'Loaded' &&
            fetchingFilteredGamesStatus === 'Loaded' && fetchingCoachTeamsStatus === 'Loaded' &&
            fetchingSeasonDivisionsStatus === 'Loaded' && fetchingUmpiresStatus === 'Loaded';
  }

  getUmpireLevelNeeded = game => {
    const { seasonDivisions } = this.props;
    const umpsRequested =
      game.umpAvailable === null || game.umpAvailable === '' ? 1 : game.umpAvailable;
    let gameLevel = 10;  //Default, no Umpire Needed
    let nextLevel;

    //Determine the lowest Level of Umpire still required for the game
    if (!game.umpPlateId)
      gameLevel =
        !seasonDivisions[game.divisionId].umpPlateLevel ? 2 : seasonDivisions[game.divisionId].umpPlateLevel;
    if ((umpsRequested >= 2) && (!game.umpBase1Id)) {
      nextLevel =
        !seasonDivisions[game.divisionId].umpBase1Level ? 1 : seasonDivisions[game.divisionId].umpBase1Level;
      gameLevel = nextLevel < gameLevel ? nextLevel : gameLevel;
    }
    if ((umpsRequested >= 3) && (!game.umpBase2Id)) {
      nextLevel =
        !seasonDivisions[game.divisionId].umpBase2Level ? 1 : seasonDivisions[game.divisionId].umpBase2Level;
      gameLevel = nextLevel < gameLevel ? nextLevel : gameLevel;
    }
    if ((umpsRequested >= 4) && (!game.umpBase2Id)) {
      nextLevel =
        !seasonDivisions[game.divisionId].umpBase3Level ? 1 : seasonDivisions[game.divisionId].umpBase3Level;
      gameLevel = nextLevel < gameLevel ? nextLevel : gameLevel;
    }
    return gameLevel;
  }

  handleCheckboxChange = event => {
    const { checked } = event.target;
    const { division, team, umpire } = this.state.filters;

    let showAllGames = checked;
    let isFiltering = checked;

    isFiltering = division !== '' || team !== '' || umpire !== '' || checked;

    this.setState(
      (prevState, props) => ({
        ...prevState,
        filteringGames: isFiltering,
        filters: { ...prevState.filters, allGames: showAllGames}
      }),
      this.handleFilterGames
    );
  }

  handleSelectChange = event => {
    const { name, value } = event.target;
    let newValue = value === 'all' ? '' : value;
    if (name === 'division') {
      const teamValue = this.state.filters.division !== value ? '' : this.state.filters.team;
      this.setState(
        (prevState, props) => ({
          ...prevState,
          filteringGames: true,
          filters: { ...prevState.filters, [name]: newValue, team: teamValue }
        }),
        this.handleFilterGames
      );
      this.props.getTeams(value, this.props.userSeason);
    } else {
      this.setState(
        (prevState, props) => ({
          ...prevState,
          filteringGames: true,
          filters: { ...prevState.filters, [name]: newValue }
        }),
        this.handleFilterGames
      );
    }
  };

  handleFilterDate = selectedDate => {
    //selectedDate is the start of the Date range
    //Get the current interval from state and calculate the end of the range
    const { interval } = this.state.filters;
    const newEndDate = this.calculateEndDate(selectedDate, interval);
    
    this.setState(
      (prevState, props) => ({
        ...prevState,
        filteringGames: true,
        filters: { ...prevState.filters, startDate: selectedDate.format(), endDate: newEndDate.format() }
      }),
      this.handleFilterGames
    );
  };

  calculateEndDate = (startDate, interval) => {
    const { userSeason, seasons } = this.props;
    let newEndDate;
    
    //Set endDate based on the interval value
    if (interval === 'end')
      newEndDate = moment(seasons[userSeason].finish);
    else
      newEndDate = moment(startDate).add(+interval, 'days');

    //Now check that the new endDate isn't beyond the end of the allowed range
    //If the User is an Admin it is the finishDate else it is the maxScheduleDate
    if (this.isAdmin())
      newEndDate = moment.min(newEndDate, moment(seasons[userSeason].finish));
    else
      newEndDate = moment.min(newEndDate, moment(seasons[userSeason].maxScheduleDate));

    return(newEndDate);
  };

  handleIntervalChange = event => {
    const { value } = event.target;
    const { startDate } = this.state.filters;

    const newEndDate = this.calculateEndDate(startDate, value);
    
    this.setState(
      (prevState) => ({
        ...prevState,
        filteringGames: true,
        filters: { ...prevState.filters, interval: value, endDate: newEndDate.format() }
      }),
      this.handleFilterGames
    );
  }

  handleUmpireChange = selectedOption => {
    if (selectedOption !== null) {
      this.setState(
        (prevState, props) => ({
          ...prevState,
          filteringGames: true,
          filters: { ...prevState.filters, umpire: selectedOption.value },
          selectedOption
        }),
        this.handleFilterGames
      );
    } else {
      this.setState(
        (prevState, props) => ({
          ...prevState,
          filteringGames: true,
          filters: { ...prevState.filters, umpire: '' },
          selectedOption: ''
        }),
        this.handleFilterGames
      );
    }
  };

  removeFilters = () => {
    this.setState({
      filters: {
        startDate: moment().format(),
        endDate: moment().add(7, 'days').format(),
        interval: '7',
        division: '',
        team: '',
        umpire: '',
        allGames: false,
        levelAssigned: '',
        levelNeeded: ''
      },
      selectedOption: ''
    });
  };

  handleFilterGames = () => {
    let endDate =
      this.state.filters.endDate === null
        ? this.state.filters.startDate
        : this.state.filters.endDate;
    endDate = endDate === null ? null : format(endDate, 'MM/DD/YYYY');
    this.props.filterGames(
      format(this.state.filters.startDate, 'MM/DD/YYYY'),
      endDate,
      this.props.userSeason,
      this.state.filters.division,
      this.state.filters.team,
      this.state.filters.umpire,
      this.state.filters.allGames,
      false
    );
  };

  handleFilterDialog = event => {
    this.setState({ ...this.state, isOpen: !this.state.isOpen });
  };

  loadAdditionalGames = () => {
    const { filteringGames } = this.state;

    //const numberOfGamesToLoad = 145;

    if(filteringGames){
      this.props.filterGames(format(this.state.filters.startDate, 'MM/DD/YYYY'), null, this.props.userSeason, null, null, null, this.state.filters.allGames, false);
    }
    this.setState({ displayAdditionalGames: true });
  };

  updateMyGames = () => {
    const { memberId, getUpdatedMyGames, teamsCoaching, userSeason } = this.props;

    const isUmpire = this.isUmpire();
    const isCoach = this.isCoach();

    let myTeams = '';
    for(let i = 0; i < teamsCoaching.length; i++){
      if(i === teamsCoaching.length - 1)
        myTeams += teamsCoaching[i].teamId;
      else
        myTeams += teamsCoaching[i].teamId + ",";
    }

    const teamIds = isCoach ? myTeams : '';
    const umpireId = isUmpire ? memberId : '';

    getUpdatedMyGames(userSeason, teamIds, umpireId, format(Date.now(), 'MM/DD/YYYY'));
  };

  handleTabChange = (event, value) => {
    let isUpdating = false;

    if (value === 'home-opening-tab') {
      this.updateMyGames();
      isUpdating = true;
    } else if (value === 'home-selection-tab') {
      this.handleFilterGames();
      isUpdating = true;
    }
    this.setState({
      ...this.state,
      tab: value,
      isOpen: false,
      filteringGames: false,
      displayAdditionalGames: false,
      updatingAny: isUpdating
    });
  };
   
  renderFilterDialog() {
    const {
      classes, userSeason, seasons, seasonDivisions, seasonDivisionsList,
      teams, teamsList, umpiresList,
    } = this.props;

    let minDate, maxDate;
    const today = moment().format();
    const tomorrow = moment().add(1, 'days').format();

    const isAdmin = this.isAdmin();

    //Set the date range of the filter based on the role
    if (isAdmin) {
      //min is earliest of today and season start.
      //max is the latest of tomorrow and season end
      minDate = (today < seasons[userSeason].start) ? today : seasons[userSeason].start;
      maxDate = (tomorrow > seasons[userSeason].finish) ? tomorrow : seasons[userSeason].finish;
    } else {
      //min is today
      //max is the max schedule date of the season.
      minDate = today;
      maxDate = seasons[userSeason].maxScheduleDate;
    }
  
    return (
      <div>
        <Tooltip id="tooltip-filter" title="Filter Games" disableFocusListener={true}>
          <Button
            className={classes.button}
            variant="contained" size="small" color="primary"
            onClick={this.handleFilterDialog}
          >
            <FilterList />
          </Button>
        </Tooltip>
        <Dialog
          open={this.state.isOpen}
          onClose={this.handleFilterDialog}
        >
          <DialogTitle className={classes.dialogSection}>Game Filters</DialogTitle>
          <DialogContent className={classes.dialogSection}>
            <Divider variant="fullWidth" />
            <Typography className={classes.formControlLabel} variant="body1">Date Range</Typography>
            <FormControlLabel
              className={classes.formControlLabel}
              style={{marginLeft: 16}}
              label={<Typography className={classes.selectLabel} variant="body1">Start:</Typography>}
              labelPlacement="start"
              control={
                <DatePicker
                  value={this.state.filters.startDate}
                  onChange={this.handleFilterDate}
                  autoOk
                  showTodayButton
                  minDate={minDate}
                  maxDate={maxDate}
                  format="ddd, MMM Do YYYY"
                />
              }
            />
            <FormControl className={classes.groupControl} style={{marginLeft: 16}}>
              <FormLabel><Typography variant="body1">End:</Typography></FormLabel>
              <RadioGroup
                style={{marginLeft: 8}}
                name="interval"
                value={this.state.filters.interval}
                onChange={this.handleIntervalChange}
              >
                <FormControlLabel 
                  value="1" 
                  control={<Radio className={classes.selectControl} color="primary"/>} 
                  label={<Typography variant="body1">1 day</Typography>}
                />
                <FormControlLabel
                  value="7"
                  control={<Radio className={classes.selectControl} color="primary"/>}
                  label={<Typography variant="body1">1 week</Typography>}
                />
                <FormControlLabel
                  value="14"
                  control={<Radio className={classes.selectControl} color="primary"/>}
                  label={<Typography variant="body1">2 weeks</Typography>}
                />
                <FormControlLabel
                  value="42"
                  control={<Radio className={classes.selectControl} color="primary"/>}
                  label={<Typography variant="body1">6 weeks</Typography>}
                />
                <FormControlLabel
                  value="end"
                  control={<Radio className={classes.selectControl} color="primary"/>}
                  label={<Typography variant="body1">End of Season</Typography>}
                />
              </RadioGroup>
            </FormControl>
            <Divider variant="fullWidth" />
            <FormControlLabel
              className={classes.formControlLabel}
              label={<Typography className={classes.selectLabel} variant="body1">Division:</Typography>}
              labelPlacement="start"
              control={
                <Select
                  classes={{select: classes.select}}
                  id="division-select"
                  value={this.state.filters.division}
                  onChange={this.handleSelectChange}
                  input={
                    <OutlinedInput
                      name="division"
                      id="division-input"
                      labelWidth={0}
                    />
                  }
                >
                  <MenuItem value="all"><Typography variant="body2">All Divisions</Typography></MenuItem>
                  {seasonDivisionsList.map(divisionId => {
                    return (
                      <MenuItem key={divisionId} value={divisionId}>
                        <Typography variant="body2">{seasonDivisions[divisionId].name}</Typography>
                      </MenuItem>
                    );
                  })}
                  </Select>
                }
              />
            <FormControlLabel
              className={classes.formControlLabel}
              label={<Typography className={classes.selectLabel} variant="body1">Team:</Typography>}
              labelPlacement="start"
              control={
                <Select
                  classes={{select: classes.select}}
                  id="team-select"
                  value={this.state.filters.team}
                  onChange={this.handleSelectChange}
                  input={
                    <OutlinedInput
                      name="team"
                      id="team-input"
                      labelWidth={0}
                    />
                  }
                >
                  <MenuItem key="teams-all" value="all"><Typography variant="body2">All Teams</Typography></MenuItem>
                  {teamsList.map(teamId => {
                    return (
                      <MenuItem key={teamId} value={teamId}>
                        <Typography variant="body2">{teams[teamId].name}</Typography>
                      </MenuItem>
                    );
                  })}
                </Select>
              }
            />
            <FormControlLabel
              className={classes.formControlLabel}
              label={<Typography variant="body1">Umpire:</Typography>}
              labelPlacement="start"
              control={
                <ComboBoxSearch
                  placeholder="Type Umpire Name"
                  items={umpiresList}
                  selectedOption={this.state.selectedOption}
                  onItemChange={selectedOption => this.handleUmpireChange(selectedOption)}
                />
              }
            />
            <FormControlLabel
              className={classes.formControlLabel}
              label={<Typography className={classes.selectLabel} variant="body1">Level Assigned:</Typography>}
              labelPlacement="start"
              control={
                <Select
                  classes={{select: classes.smallSelect}}
                  id="levelAssigned"
                  value={this.state.filters.levelAssigned}
                  onChange={this.handleSelectChange}
                  input={
                    <OutlinedInput
                      name="levelAssigned"
                      id="levelAssigned-input"
                      labelWidth={0}
                    />
                  }
                >
                  <MenuItem key="teams-all" value="all"><Typography variant="body2">All Levels</Typography></MenuItem>
                  {[1,2,3,4,5,6,7].map(i => {
                    return (
                      <MenuItem key={`level-${i}`} value={i}>
                        <Typography variant="body2">{i}</Typography>
                      </MenuItem>
                    );
                  })}
                </Select>
              }
            />
            <FormControlLabel
              className={classes.formControlLabel}
              label={<Typography className={classes.selectLabel} variant="body1">Level Needed:</Typography>}
              labelPlacement="start"
              control={
                <Select
                  classes={{select: classes.smallSelect}}
                  id="levelNeeded"
                  value={this.state.filters.levelNeeded}
                  onChange={this.handleSelectChange}
                  input={
                    <OutlinedInput
                      name="levelNeeded"
                      id="levelNeeded-input"
                      labelWidth={0}
                    />
                  }
                >
                  <MenuItem key="teams-all" value="all"><Typography variant="body2">All Levels</Typography></MenuItem>
                  {[1,2,3,4,5,6,7].map(i => {
                    return (
                      <MenuItem key={`level-${i}`} value={i}>
                        <Typography variant="body2">{i}</Typography>
                      </MenuItem>
                    );
                  })}
                </Select>
              }
            />
            <FormControlLabel
              className={classes.formControlLabel}
              label={<Typography className={classes.selectLabel} variant="body1">Show All Games:</Typography>}
              labelPlacement="start"
              control={
                <Checkbox
                  className={classes.selectControl}
                  onChange={this.handleCheckboxChange}
                  checked={this.state.filters.allGames}
                  color="primary"
                />   
              }
            />
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Tooltip id="tooltip-clear" title="Clear Game Filters">
              <Button
                className={classes.button}
                variant="contained" size="small" color="primary"
                onClick={this.removeFilters}
              >
                Clear
              </Button>
            </Tooltip>
            <Tooltip id="tooltip-ok" title="Apply Filters and Close">
              <Button
                className={classes.button}
                variant="contained" size="small" color="primary" style={{marginLeft: 5}}
                onClick={this.handleFilterDialog}  // toggles isOpen to false
              >
                OK
              </Button>
            </Tooltip>
          </DialogActions>
        </Dialog>
      </div>
    );
  };

  render() {
    const {
      games,
      myGamesList,
      filteredGamesList,
      teamsCoaching,
      teamsVolunteering,
      memberId,
      umpires,
      umpiresList,
      fetchingUmpiresStatus
    } = this.props;
    const { classes } = this.props;
    const {
      //displayAdditionalGames,
      tab,
      filteringGames,
    } = this.state;
    let displayedGameList = [];

    const isCoach = this.isCoach();
    const isUmpire = this.isUmpire();
    const isAdmin = this.isAdmin();

    if (this.allLoaded())  {
      if (tab === 'home-selection-tab' || (isAdmin && !isCoach && !isUmpire)) {
        if (filteringGames) {
          if(this.state.filters.levelAssigned !== '' && this.state.filters.levelNeeded !== ''){
            filteredGamesList
            .filter(game => (this.state.filters.levelAssigned === games[game].umpPlateLevel ||
                this.state.filters.levelAssigned === games[game].umpBase1Level ||
                this.state.filters.levelAssigned === games[game].umpBase2Level ||
                this.state.filters.levelAssigned === games[game].umpBase3Level) &&
                (this.getUmpireLevelNeeded(games[game]) <= this.state.filters.levelNeeded))
            .map(game => {
              return displayedGameList.push(game);
            });
          } else if(this.state.filters.levelAssigned !== '' && this.state.filters.levelNeeded === ''){
            filteredGamesList
            .filter(game => this.state.filters.levelAssigned === games[game].umpPlateLevel ||
                this.state.filters.levelAssigned === games[game].umpBase1Level ||
                this.state.filters.levelAssigned === games[game].umpBase2Level ||
                this.state.filters.levelAssigned === games[game].umpBase3Level)
            .map(game => {
              return displayedGameList.push(game);
            });
          } else if(this.state.filters.levelAssigned === '' && this.state.filters.levelNeeded !== ''){
            filteredGamesList
            .filter(game => this.getUmpireLevelNeeded(games[game]) <= this.state.filters.levelNeeded)
            .map(game => {
              return displayedGameList.push(game);
            });
          }else {
            displayedGameList = filteredGamesList;
          }
        } else {
          displayedGameList = filteredGamesList;
        }
      } else if (tab === 'home-opening-tab') {
        displayedGameList = myGamesList;
      }
    }

    //const hideLoadMoreGamesClass = filteringGames ? 'hide' : '';

    return (
        <div className={classes.root}>
          <Grid container direction="row" justify="center" alignItems="flex-start" spacing={16} display="flex">
            <Grid item sm={12} md={8} lg={9}>
              <Card className={classes.gamesCard}>
                <div className={classes.gamesHeader}>
                  {isAdmin ? (
                    <div>
                      {isCoach || isUmpire ?
                        <Tabs
                          indicatorColor="primary"
                          textColor="primary"
                          value={this.state.tab}
                          onChange={this.handleTabChange}
                        >
                          <Tab value="home-opening-tab" label="My Games" />
                          <Tab value="home-selection-tab" label="Assign Games" />
                        </Tabs>
                      :
                        <Tabs
                          indicatorColor="primary"
                          textColor="primary"
                          value="home-selection-tab"
                          onChange={this.handleTabChange}
                        >
                          <Tab value="home-selection-tab" label="Assign Games" />
                        </Tabs>
                      }
                    </div>
                  ) : (
                    <div>
                      {isCoach || isUmpire ?
                        <Tabs
                          indicatorColor="primary"
                          textColor="primary"
                          value={this.state.tab}
                          onChange={this.handleTabChange}
                        >
                          <Tab value="home-opening-tab" label="My Games" />
                          <Tab value="home-selection-tab" label="All Games" />
                        </Tabs>
                      :
                        <Tabs
                          indicatorColor="primary"
                          textColor="primary"
                          value="home-selection-tab"
                          onChange={this.handleTabChange}
                        >
                          <Tab value="home-selection-tab" label="All Games" />
                        </Tabs>
                      }
                    </div>
                  )}
                  <div>
                    {this.state.tab === "home-selection-tab" ? this.renderFilterDialog() : ''}
                  </div>
                </div>
                <div className={classes.divider}>
                  <Divider/>
                </div>
                {this.allLoaded() ? (
                  <div>
                    {displayedGameList !== null && displayedGameList.length > 0 && Object.values(games).length > 0 ? (
                      <List className={classes.gameTable}>
                        {displayedGameList.map((id) => {
                          //Determine if the User is the Coach of this Game
                          let isGameCoach = false;
                          const game = games[id];
                          teamsCoaching.forEach((team) => {
                            if (game.homeTeamId === team.teamId || game.awayTeamId === team.teamId) isGameCoach = true;
                          });
                          //Determine if the User is a Volunteer for a Team in this Game
                          let isTeamVolunteer = false;
                          teamsVolunteering.forEach((team) => {
                            if (game.homeTeamId === team.teamId || game.awayTeamId === team.teamId) isTeamVolunteer = true;
                          });
                          return (
                          <ListItem key={id}>
                            <GameCard
                              games={games}
                              gameId={id}
                              umpires={umpires}
                              umpiresList={umpiresList}
                              fetchingUmpiresStatus={fetchingUmpiresStatus}
                              memberId={memberId}
                              isUmpire={isUmpire}
                              isAdmin={isAdmin}
                              isCoach={isGameCoach}
                              isTeamVolunteer={isTeamVolunteer}
                              assignUmpToGame={this.props.assignUmpToGame}
                              removeUmp={this.props.removeUmp}
                              updateUmpAvailableForGame={this.props.updateUmpAvailableForGame}
                              allowsAssigning={this.state.tab === 'home-selection-tab'}
                              seasonDivisions={this.props.seasonDivisions}
                              isRow={true}
                            />
                          </ListItem>)
                        })}
                      </List>
                    ) : (
                      <div className="table" item="true" sm="true" >
                        <div className="empty-table"> Not assigned to any games. </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="empty-table"> Loading Games... </div>
                )}
              </Card>
            </Grid>
            <Grid item sm={12} md={4} lg={3}>
              <Grid container alignItems="stretch" spacing={16}>
                <Grid item sm={6} md={12} lg={12}>
                  <WeatherContainer />
                </Grid>
                <Grid item sm={6} md={12} lg={12}>
                  {isUmpire ? <UserStatisticsContainer /> : ''}
                </Grid>
                <Grid item sm={6} md={12} lg={12}>
                  {isUmpire ? <MentorMenteeCardContainer /> : ''}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
    );
  }
}

Home.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired
};

//export default Home;
export default withStyles(styles, { withTheme: true })(Home);



// WEBPACK FOOTER //
// ./src/components/HomeGridV2.js